import { Configuration, LogLevel, RedirectRequest } from "@azure/msal-browser";
import { appConfig } from "appConfig";

const authorityBaseUrl = `https://${appConfig.authTenantName}.b2clogin.com/${appConfig.authTenant}`;
const scopeBaseUrl = `https://${appConfig.authTenant}`;

export const ADB2C_ERROR_CODES = {
  USER_CANCELLATION: "AADB2C90091",
  FORGOTTEN_PASSWORD: "AADB2C90118",
  INTERACTION_IN_PROGRESS: "interaction_in_progress",
};

export const b2cPolicies = {
  names: {
    signIn: `${appConfig.authUserFlowSignIn}`,
    forgotPassword: `${appConfig.authUserFlowPasswordReset}`,
  },
  authorities: {
    signIn: {
      authority: `${authorityBaseUrl}/${appConfig.authUserFlowSignIn}`,
    } as RedirectRequest,
    forgotPassword: {
      authority: `${authorityBaseUrl}/${appConfig.authUserFlowPasswordReset}`,
      redirectStartPage: "/",
    } as RedirectRequest,
  },
  authorityDomain: `${appConfig.authTenantName}.b2clogin.com`,
};

export const msalConfig: Configuration = {
  auth: {
    clientId: `${appConfig.authClientId}`, //"a312c1bd-4ccd-4578-ba4b-2876d87fd3f7", // This is the ONLY mandatory field that you need to supply.
    authority: b2cPolicies.authorities.signIn.authority, // Choose B2C_1_lc_sign_in as your default authority.
    knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
    redirectUri: "/auth-redirect", // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
    postLogoutRedirectUri: "/", // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: true, // If "true", will navigate back to the original request location before processing the auth code response.
  },
  cache: {
    cacheLocation: "sessionStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level: LogLevel, message: any, containsPii: any) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
        }

        if (appConfig.authUseVerboseLogging === "true") {
          switch (level) {
            case LogLevel.Info:
              console.info(message);
              return;
            case LogLevel.Verbose:
              console.debug(message);
              return;
            case LogLevel.Warning:
              console.warn(message);
              return;
          }
        }
      },
    },
  },
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
  apiBO: {
    scopes: [`${scopeBaseUrl}/${appConfig.authScopesBoRead}`, `${scopeBaseUrl}/${appConfig.authScopesBoWrite}`],
  },
  apiCDS: {
    scopes: [`${scopeBaseUrl}/${appConfig.authScopesCdsRead}`],
  },
  apiLRS: {
    scopes: [`${scopeBaseUrl}/${appConfig.authScopesLrsRead}`, `${scopeBaseUrl}/${appConfig.authScopesLrsWrite}`],
  },
  apiASS: {
    scopes: [`${scopeBaseUrl}/${appConfig.authScopesAssRead}`, `${scopeBaseUrl}/${appConfig.authScopesAssWrite}`],
  },
  apiFFS: {
    scopes: [`${scopeBaseUrl}/${appConfig.authScopesFfsRead}`],
  },
  apiSIGR: {
    scopes: [`${scopeBaseUrl}/${appConfig.authScopesSigRRead}`, `${scopeBaseUrl}/${appConfig.authScopesSigRWrite}`],
  },
  apiCOM: {
    scopes: [`${scopeBaseUrl}/${appConfig.authScopesComRead}`, `${scopeBaseUrl}/${appConfig.authScopesComWrite}`],
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: [], // No extra scopes needed here at the moment
};

export const getScope = (url: string) => {
  if (url.includes(appConfig.boApiUrl!)) return protectedResources.apiBO.scopes;

  if (url.includes(appConfig.cdsApiUrl!)) return protectedResources.apiCDS.scopes;

  if (url.includes(appConfig.lrsApiUrl!)) return protectedResources.apiLRS.scopes;

  if (url.includes(appConfig.assApiUrl!)) return protectedResources.apiASS.scopes;

  if (url.includes(appConfig.ffsApiUrl!)) return protectedResources.apiFFS.scopes;

  if (url.includes(appConfig.sigrApiUrl!)) return protectedResources.apiSIGR.scopes;

  if (url.includes(appConfig.comApiUrl!)) return protectedResources.apiCOM.scopes;
};
